/**
 * Property Types Management API Hooks
 * React Query hooks for property types CRUD operations with infinite scroll
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import { Endpoints, EndpointHelpers } from '@/config/endpoints';
import { DEFAULT_PAGE_SIZE } from '@/config/constants';
import { toast } from '@/hooks/use-toast';
import type {
  ApiPropertyType,
  CreatePropertyTypeRequest,
  UpdatePropertyTypeRequest,
  PropertyTypesListParams,
  ItemResponse,
  ListPaginated,
} from '@/types/api';

// ============================================================================
// Query Keys
// ============================================================================

export const propertyTypeQueryKeys = {
  all: ['propertyTypes'] as const,
  lists: () => [...propertyTypeQueryKeys.all, 'list'] as const,
  list: (params: PropertyTypesListParams) =>
    [...propertyTypeQueryKeys.lists(), params] as const,
  infinite: (params: PropertyTypesListParams) =>
    [...propertyTypeQueryKeys.lists(), 'infinite', params] as const,
} as const;

// ============================================================================
// Property Types Infinite Query (for infinite scroll)
// ============================================================================

export function usePropertyTypesInfiniteQuery(
  params: PropertyTypesListParams = {}
) {
  return useInfiniteQuery({
    queryKey: propertyTypeQueryKeys.infinite(params),
    queryFn: async ({
      pageParam = 1,
    }): Promise<ListPaginated<ApiPropertyType>> => {
      const client = new AxiosClient<undefined, ApiPropertyType>({
        endpoint: Endpoints.PROPERTY_TYPES_LIST,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: pageParam,
          page_size: params.page_size || DEFAULT_PAGE_SIZE,
          ...(params.search && { search: params.search }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch property types');
      }

      return response;
    },
    getNextPageParam: (lastPage: ListPaginated<ApiPropertyType>) => {
      return lastPage.pagination.next ?? undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// Create Property Type Mutation
// ============================================================================

interface UseCreatePropertyTypeMutationOptions {
  onSuccessCallback?: () => void;
}

export function useCreatePropertyTypeMutation(
  options: UseCreatePropertyTypeMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      propertyTypeData: CreatePropertyTypeRequest
    ): Promise<ItemResponse<ApiPropertyType>> => {
      const client = new AxiosClient<
        CreatePropertyTypeRequest,
        ApiPropertyType
      >({
        endpoint: Endpoints.PROPERTY_TYPE_CREATE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.post(propertyTypeData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create property type');
      }

      return response;
    },

    onSuccess: (response: ItemResponse<ApiPropertyType>) => {
      toast({
        title: 'Property Type Created',
        description: `${response.data.name} has been successfully created.`,
      });

      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: propertyTypeQueryKeys.all });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Create property type error:', error);

      let errorMessage = 'Failed to create property type. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          // Handle validation errors
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Update Property Type Mutation
// ============================================================================

interface UseUpdatePropertyTypeMutationOptions {
  onSuccessCallback?: () => void;
}

export function useUpdatePropertyTypeMutation(
  propertyTypeId: string | number,
  options: UseUpdatePropertyTypeMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      propertyTypeData: UpdatePropertyTypeRequest
    ): Promise<ItemResponse<ApiPropertyType>> => {
      const client = new AxiosClient<
        UpdatePropertyTypeRequest,
        ApiPropertyType
      >({
        endpoint: EndpointHelpers.updatePropertyType(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(propertyTypeData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to update property type');
      }

      return response;
    },

    onSuccess: (response: ItemResponse<ApiPropertyType>) => {
      toast({
        title: 'Property Type Updated',
        description: `${response.data.name} has been successfully updated.`,
      });

      // Invalidate all property type queries
      queryClient.invalidateQueries({ queryKey: propertyTypeQueryKeys.all });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update property type error:', error);

      let errorMessage = 'Failed to update property type. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          // Handle validation errors
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Delete Property Type Mutation
// ============================================================================

interface UseDeletePropertyTypeMutationOptions {
  onSuccessCallback?: () => void;
}

export function useDeletePropertyTypeMutation(
  options: UseDeletePropertyTypeMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      propertyTypeId: string | number
    ): Promise<ItemResponse<{ id: number; name: string }>> => {
      const client = new AxiosClient<undefined, { id: number; name: string }>({
        endpoint: EndpointHelpers.deletePropertyType(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        // Create an error that includes the full response data
        const error = new Error(
          response.error || 'Failed to delete property type'
        ) as Error & {
          response?: { data: ItemResponse<unknown> };
        };
        error.response = { data: response };
        throw error;
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Property Type Deleted',
        description: 'Property type has been successfully deleted.',
      });

      // Invalidate all property type queries
      queryClient.invalidateQueries({ queryKey: propertyTypeQueryKeys.all });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete property type error:', error);

      // Default error message
      let errorTitle = 'Delete Failed';
      let errorMessage = 'Failed to delete property type. Please try again.';

      // Extract error details from backend response
      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        // Use the error as title if available
        if (errorData.error) {
          errorTitle = errorData.error;
        }

        // Use the detail message from backend if available (it has the specific reason)
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive',
        duration: 7000, // Show error longer so user can read the full message
      });
    },
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get property types list from cache without triggering a network request
 */
export function usePropertyTypesCache(params: PropertyTypesListParams = {}) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<ListPaginated<ApiPropertyType>>(
    propertyTypeQueryKeys.list(params)
  );
}
